// MST
import { types, flow, cast } from 'mobx-state-tree';

// Models
import { RoleListRef } from 'models/back-office/master/role/role.store';

// Interfaces
import { IAuthApiChangeRoleResponse, IAuthApiGetAuthenticatedUserResponse } from 'interfaces/auth/auth-api.interface';
import { IAuthChangeRoleAttrs, IAuthGetAuthenticatedUserAttrs } from 'interfaces/auth/auth-view.interface';
import { IAuthAuthenticatedUser, IAuthStoreInitialize, IAuthOidc } from 'interfaces/auth';

// Constant
import { USER_STATUS } from 'constant/shared.constant';

// Extensions
import { withEnvironment } from 'models/extensions/with-environment';

// Lodash
import cloneDeep from 'lodash/cloneDeep';

// Constant
import {
  LOCAL_STORAGE_ACCESS_TOKEN,
  LOCAL_STORAGE_AUTHENTICATED_USER,
  LOCAL_STORAGE_IS_AUTHENTICATED,
} from 'constant/local-storage.constant';

// Lodash
import pick from 'lodash/pick';

// Types
const { model, enumeration, array, optional, maybe, maybeNull, number, string, boolean } = types;

// Auth Props
const authProps = {
  id: maybeNull(number),
  name: maybeNull(string),
  email: maybeNull(string),
  npk: maybeNull(string),
  nik: maybeNull(string),
  companyName: maybeNull(string),
  companyId: maybeNull(number),
  branchName: maybeNull(string),
  branchId: maybeNull(number),
  divisionName: maybeNull(string),
  divisionId: maybeNull(number),
  profilePhoto: maybeNull(string),
  idCardPhoto: maybeNull(string),
  phoneNumber: maybeNull(string),
  isRegistered: maybeNull(boolean),
  isPhoneNumberValidated: maybeNull(boolean),
  isProfileCompleted: maybeNull(boolean),
  supervisorEmail: maybeNull(string),
  supervisorName: maybeNull(string),
  address: maybeNull(string),
  birthDate: maybeNull(string),
  status: maybeNull(enumeration('UserStatus', Object.values(USER_STATUS))),
  accountStatus: maybeNull(boolean),
  roles: maybeNull(optional(array(RoleListRef), [])),
  isValidated: maybe(maybeNull(boolean)),
  fieldId: maybeNull(number),
  fieldName: maybeNull(string)
};

const AuthenticatedUser = model('AuthenticatedUser').props(
  pick(authProps, [
    'id',
    'name',
    'email',
    'profilePhoto',
    'isRegistered',
    'isPhoneNumberValidated',
    'isProfileCompleted',
    'status',
    'accountStatus',
    'roles',
    'isValidated',
    'fieldId',
    'fieldName'
  ]),
);

const AuthStoreModel = model('Auth')
  .props({
    auth_isLoading: optional(boolean, false),
    auth_isVerifyLoading: optional(boolean, false),
    auth_isAuthenticated: optional(boolean, false),
    auth_accessToken: optional(string, ''),
    auth_refreshToken: optional(string, ''),
    auth_authenticatedUser: optional(AuthenticatedUser, {}),
  })
  .extend(withEnvironment)
  .actions(self => ({
    auth_SET_LOADING: (payload: boolean): void => {
      self.auth_isLoading = payload;
    },
    auth_VERIFY_LOADING: (payload: boolean): void => {
      self.auth_isVerifyLoading = payload;
    },
    auth_SET_TOKEN: (payload: IAuthOidc): void => {
      self.auth_accessToken = cast(payload.accessToken);
      self.auth_refreshToken = cast(payload.refreshToken);
    },
    auth_INITIALIZE: (payload: IAuthStoreInitialize): void => {
      self.auth_accessToken = cast(payload.auth_accessToken);
      self.auth_refreshToken = cast(payload.auth_refreshToken);
      self.auth_authenticatedUser = cast(payload.auth_authenticatedUser);
      self.auth_isAuthenticated = cast(payload.auth_isAuthenticated);
    },
    auth_SET_AUTHENTICATED: (payload: boolean): void => {
      self.auth_isAuthenticated = payload;
    },
    auth_SET_AUTHENTICATED_USER: (payload: IAuthAuthenticatedUser): void => {
      self.auth_authenticatedUser = cast(cloneDeep(payload));
    },
    auth_LOGOUT: (): void => {
      self.auth_accessToken = '';
      self.auth_refreshToken = '';
      self.auth_authenticatedUser = cast({});
      self.auth_isAuthenticated = false;
    },
  }))
  .actions(self => ({
    /**
     * @description Set auth token
     *
     * @param {IAuthOidc} payload
     *
     * @return {any} {any}
     */
    auth_setToken: function (payload: IAuthOidc) {
      self.auth_SET_TOKEN({ accessToken: payload.accessToken, refreshToken: payload.refreshToken || '' });

      localStorage.setItem(LOCAL_STORAGE_ACCESS_TOKEN, payload.accessToken);
    },

    /**
     * @description Get authenticated user
     *
     * @param {IAuthGetAuthenticatedUserAttrs} payload
     *
     * @return {any} any
     */
    auth_getAuthenticatedUser: flow(function* (payload: IAuthGetAuthenticatedUserAttrs) {
      self.auth_VERIFY_LOADING(true);

      const response: IAuthApiGetAuthenticatedUserResponse = yield self.environment.authApi.auth_getAuthenticatedUser();
      if (response.ok) {
        const authenticatedUser = response.res.data;
        const { isPhoneNumberValidated, isRegistered, status, accountStatus, isProfileCompleted } = authenticatedUser;
        const isValidated = Boolean(
          isProfileCompleted && isPhoneNumberValidated && isRegistered && status === USER_STATUS.APPROVED && accountStatus,
        );
        const mapAuthenticatedUser = {
          ...authenticatedUser,
          isValidated,
        };

        // Set to store
        self.auth_SET_AUTHENTICATED_USER(mapAuthenticatedUser);

        // Append the authenticated user to local storage
        const previousLspAuthLocalStorage = localStorage.getItem(LOCAL_STORAGE_AUTHENTICATED_USER)!;
        localStorage.setItem(
          LOCAL_STORAGE_AUTHENTICATED_USER,
          JSON.stringify({ ...JSON.parse(previousLspAuthLocalStorage), ...mapAuthenticatedUser }),
        );

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess();
      }

      if (!response.ok) {
        // Do when response failed
        if (payload?.onError) payload.onError(response);
      }

      self.auth_VERIFY_LOADING(false);
    }),

    /**
     * @description Change role
     *
     * @param {IAuthChangeRoleAttrs} payload
     *
     * @return {any} any
     */
    auth_changeRole: flow(function* (payload: IAuthChangeRoleAttrs) {
      self.auth_SET_LOADING(true);

      const response: IAuthApiChangeRoleResponse = yield self.environment.authApi.auth_changeRole({
        body: { ...payload.body },
        requestConfig: { ...payload.requestConfig },
      });
      if (response.ok) {
        // When user changing the role, make user to true
        self.auth_SET_AUTHENTICATED(true);
        localStorage.setItem(LOCAL_STORAGE_IS_AUTHENTICATED, 'true');

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess();
      }

      if (!response.ok) {
        // Do when response failed
        if (payload?.onError) payload.onError();
      }

      self.auth_SET_LOADING(false);
    }),

    /**
     * @description Login
     *
     * @return {any} any
     */
    auth_logout: () => {
      self.auth_LOGOUT();

      localStorage.clear();
    },
  }));

export const createAuthStoreDefaultModel = () => optional(AuthStoreModel, {});
